'use client'

import * as React from 'react'
import { Button } from '@repo/studio-design-system-ui-button'
import { ActivityIcon } from '@repo/studio-design-system-ui-icons/general/activity'
import { Download01Icon } from '@repo/studio-design-system-ui-icons/general/download-01'
import { FolderIcon } from '@repo/studio-design-system-ui-icons/files/folder'
import { Settings02Icon } from '@repo/studio-design-system-ui-icons/general/settings-02'
import { AIStudioVisualMark } from '@repo/studio-design-system-ui-ai-studio-logo/visual-mark'

import type { DownloadPageContent, SharedContent, ToolSlug } from '../lib/content/types'
import { SectionContainer } from './primitives'
import { ToolIcon } from './tool-icon'

const FOOTER_ITEMS = [
  { id: 'activity', Icon: ActivityIcon },
  { id: 'models', Icon: Download01Icon },
  { id: 'outputs', Icon: FolderIcon },
  { id: 'settings', Icon: Settings02Icon },
] as const

type FooterPanel = (typeof FOOTER_ITEMS)[number]['id']

export function ProductStageMock({
  mock,
  tools,
}: {
  mock: SharedContent['mock']
  tools: DownloadPageContent['tools']['items']
}) {
  const [selectedSlug, setSelectedSlug] = React.useState<ToolSlug>(tools[0].slug)
  const [panel, setPanel] = React.useState<FooterPanel | null>(null)
  const selectedTool = tools.find(tool => tool.slug === selectedSlug) ?? tools[0]

  return (
    <section className="pb-2 pt-12">
      <SectionContainer className="max-w-[1040px]">
        <div className="rounded-[24px] border border-sd-border bg-sd-surface p-3 sm:p-6">
          <div className="overflow-hidden rounded-[16px] border border-sd-border bg-theme_semantic_surface_primary-background-elevated">
            <div className="flex flex-wrap items-center gap-2 border-b border-sd-border bg-sd-surface px-4 py-3">
              <AIStudioVisualMark className="size-5 text-sd-accent" />
              <span className="text-sm font-medium text-sd-ink">{mock.title}</span>
              <span className="ml-auto rounded-full bg-sd-raised px-3 py-1 text-xs text-sd-green">
                {mock.status}
              </span>
            </div>
            <div className="grid md:grid-cols-[230px_1fr]">
              <div className="bg-sd-surface p-4 md:border-r md:border-sd-border">
                <p className="mb-3 text-xs uppercase tracking-wider text-sd-slate">
                  {mock.freeModels}
                </p>
                <ul className="grid gap-1 sm:grid-cols-2 md:grid-cols-1">
                  {tools.map(tool => (
                    <li key={tool.slug}>
                      <button
                        type="button"
                        aria-pressed={tool.slug === selectedSlug}
                        onClick={() => setSelectedSlug(tool.slug)}
                        className={`flex w-full items-center gap-3 rounded-[8px] p-3 text-left text-sm transition-colors hover:bg-sd-raised ${tool.slug === selectedSlug ? 'bg-sd-raised text-sd-ink' : 'text-sd-slate'}`}
                      >
                        <span className="shrink-0 text-sd-accent [&_svg]:size-5">
                          <ToolIcon slug={tool.slug} />
                        </span>
                        {tool.name}
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 rounded-[12px] bg-sd-raised p-3 text-xs text-sd-green">
                  {mock.note}
                </p>
              </div>
              <div className="flex min-h-[360px] flex-col items-center justify-center gap-5 p-6 text-center">
                <span className="rounded-full bg-sd-raised px-4 py-2 text-sm text-sd-accent">
                  {selectedTool.name}
                </span>
                <span className="text-sd-accent [&_svg]:size-16">
                  <ToolIcon slug={selectedSlug} />
                </span>
                <p className="max-w-sm text-sm text-sd-slate">{selectedTool.desc}</p>
                <p className="text-xs text-sd-green">{mock.noCard}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 border-t border-sd-border bg-sd-surface p-2">
              {FOOTER_ITEMS.map(({ id, Icon }) => (
                <Button
                  key={id}
                  type="button"
                  size="sm"
                  variant={panel === id ? 'secondary' : 'ghost'}
                  aria-pressed={panel === id}
                  onClick={() => setPanel(panel === id ? null : id)}
                >
                  <Icon />
                  {mock.footer[id].title}
                </Button>
              ))}
            </div>
            {panel ? (
              <div className="border-t border-sd-border bg-sd-raised p-5" aria-live="polite">
                <h3 className="text-sm font-semibold text-sd-ink">{mock.footer[panel].title}</h3>
                <p className="mt-1 text-sm text-sd-slate">{mock.footer[panel].body}</p>
              </div>
            ) : null}
          </div>
        </div>
      </SectionContainer>
    </section>
  )
}
